import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardActionArea,
  CardMedia,
  Chip,
  IconButton,
  Typography,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { LandingPartner } from '../../types/partner';

interface PartnerCarouselProps {
  title: string;
  subtitle?: string;
  partners: LandingPartner[];
  /** Background shade of the section, used to alternate contrast between stacked carousels */
  bgcolor?: string;
}

const CARD_WIDTH = 280;
const SCROLL_AMOUNT = CARD_WIDTH + 24;

export default function PartnerCarousel({
  title,
  subtitle,
  partners,
  bgcolor = '#0f1013',
}: PartnerCarouselProps) {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState(false);

  const scrollBy = (amount: number) => {
    scrollRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setCanScroll(el.scrollWidth > el.clientWidth + 1);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(el);
    window.addEventListener('resize', checkOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
  }, [partners]);

  return (
    <Box sx={{ py: 7, px: { xs: 2, md: 6 }, bgcolor }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontFamily: 'GFS Didot', fontWeight: 'bold', color: '#fff' }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body1"
              sx={{ mt: 1, color: 'rgba(255,255,255,0.65)' }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {canScroll && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              aria-label={`Scroll ${title} left`}
              onClick={() => scrollBy(-SCROLL_AMOUNT)}
              sx={{
                border: '1px solid rgba(255,255,255,0.2)',
                color: '#fff',
                '&:hover': {
                  borderColor: '#dd8709',
                  bgcolor: 'rgba(221,135,9,0.1)',
                },
              }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label={`Scroll ${title} right`}
              onClick={() => scrollBy(SCROLL_AMOUNT)}
              sx={{
                border: '1px solid rgba(255,255,255,0.2)',
                color: '#fff',
                '&:hover': {
                  borderColor: '#dd8709',
                  bgcolor: 'rgba(221,135,9,0.1)',
                },
              }}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          gap: 3,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          pb: 1,
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'rgba(255,255,255,0.2)',
            borderRadius: 3,
          },
        }}
      >
        {partners.map((partner) => (
          <Card
            key={`${partner.type}-${partner.id}`}
            sx={{
              minWidth: CARD_WIDTH,
              maxWidth: CARD_WIDTH,
              flexShrink: 0,
              scrollSnapAlign: 'start',
              borderRadius: 3,
              bgcolor: '#1b1c22',
              border: '1px solid rgba(255,255,255,0.08)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 24px rgba(0,0,0,0.5)',
              },
            }}
            elevation={0}
          >
            <CardActionArea onClick={() => navigate(partner.profileUrl)}>
              <Box sx={{ position: 'relative' }}>
                <CardMedia
                  component="img"
                  image={partner.image}
                  alt={partner.name}
                  sx={{ height: 170, objectFit: 'cover' }}
                />
                <Chip
                  label={partner.type === 'shop' ? 'Shop' : 'Independent'}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    bgcolor: '#dd8709',
                    color: '#fff',
                    fontWeight: 'bold',
                  }}
                />
              </Box>
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'bold', mb: 0.5, color: '#fff' }}
                  noWrap
                >
                  {partner.name}
                </Typography>
                <Box
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}
                >
                  <LocationOnIcon
                    fontSize="small"
                    sx={{ color: '#dd8709', mt: 0.2 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255,255,255,0.65)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {partner.location}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mt: 0.5,
                  }}
                >
                  <AccessTimeIcon fontSize="small" sx={{ color: '#dd8709' }} />
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.65)' }}
                  >
                    {partner.openingHours}
                  </Typography>
                </Box>
              </Box>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
